import React, { useEffect, useMemo, useState } from "react";
import client, { apiError } from "@/api/client";
import { useBranch } from "@/context/BranchContext";
import { numberFmt } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, ClipboardCheck, AlertTriangle, Loader2 } from "lucide-react";

export default function OpnameBulk() {
  const { effectiveBranch, isAllBranch, currentBranchName } = useBranch();
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [catFilter, setCatFilter] = useState("all");
  const [physical, setPhysical] = useState({});
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => client.get("/stock", { params: effectiveBranch ? { branch_id: effectiveBranch } : {} }).then((r) => {
    setRows(r.data);
    const init = {};
    r.data.forEach((p) => { init[p.id] = ""; });
    setPhysical(init);
  });
  useEffect(() => { if (effectiveBranch) load(); /* eslint-disable-next-line */ }, [effectiveBranch]);
  useEffect(() => { client.get("/categories").then((r) => setCats(r.data)); }, []);

  const filtered = useMemo(() => rows.filter((p) =>
    p.name.toLowerCase().includes(q.toLowerCase()) &&
    (catFilter === "all" || p.category_id === catFilter)
  ), [rows, q, catFilter]);
  const changedItems = rows.filter((p) => physical[p.id] !== "" && physical[p.id] != null && Number(physical[p.id]) !== p.stock_qty);

  const submit = async () => {
    if (!changedItems.length) { toast.error("Tidak ada selisih untuk disimpan"); return; }
    setSaving(true);
    try {
      await client.post("/stock/opname", {
        branch_id: effectiveBranch,
        items: changedItems.map((p) => ({ product_id: p.id, physical_qty: Number(physical[p.id]), reason: "stock opname massal" })),
        note: "Opname massal",
      });
      toast.success(`Opname ${changedItems.length} produk tersimpan`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (isAllBranch && !effectiveBranch) {
    return <div className="flex h-full items-center justify-center p-6 text-center text-muted-foreground"><div><AlertTriangle className="mx-auto mb-2 h-8 w-8 opacity-40" />Pilih cabang untuk melakukan opname massal.</div></div>;
  }

  return (
    <div className="p-6 space-y-5 animate-in-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Opname Massal</h1>
          <p className="text-sm text-muted-foreground">{currentBranchName} · Isi hasil hitung fisik per baris, lalu simpan sekaligus</p>
        </div>
        <Button onClick={submit} disabled={saving || !changedItems.length} data-testid="submit-opname-bulk">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ClipboardCheck className="mr-2 h-4 w-4" />}
          Simpan ({changedItems.length} selisih)
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk…" className="pl-9" data-testid="opname-search" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-48" data-testid="opname-cat-filter"><SelectValue placeholder="Semua Kategori" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kategori</SelectItem>
            {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Produk</TableHead>
              <TableHead>Satuan</TableHead>
              <TableHead className="text-right">Stok Sistem</TableHead>
              <TableHead className="w-40 text-right">Hitung Fisik</TableHead>
              <TableHead className="text-right">Selisih</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((p) => {
              const phys = physical[p.id];
              const diff = phys === "" || phys == null ? null : Number(phys) - p.stock_qty;
              return (
                <TableRow key={p.id} className={diff ? "bg-amber-50/50" : "hover:bg-slate-50"} data-testid={`opname-bulk-row-${p.id}`}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell>{p.base_unit}</TableCell>
                  <TableCell className="text-right font-mono">{numberFmt(p.stock_qty)}</TableCell>
                  <TableCell className="text-right">
                    <Input type="number" value={phys} onChange={(e) => setPhysical((s) => ({ ...s, [p.id]: e.target.value }))}
                      className="h-9 w-28 text-right font-mono" placeholder="—" data-testid={`opname-input-${p.id}`} />
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {diff == null ? <span className="text-muted-foreground">-</span> :
                      <span className={diff === 0 ? "text-muted-foreground" : diff < 0 ? "text-rose-600 font-semibold" : "text-emerald-600 font-semibold"}>
                        {diff > 0 ? "+" : ""}{numberFmt(diff)}
                      </span>}
                  </TableCell>
                </TableRow>
              );
            })}
            {!filtered.length && <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">Tidak ada produk</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
